import React, { useState, useEffect } from 'react';
import { db } from '../db/database';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RichTextEditor from '../components/editor/RichTextEditor';
import { useUI } from '../context/UIContext';
import {
  testCloudConnection,
  pushWorkspaceBackup,
  restoreLatestBackup,
} from '../services/cloudBackup';
import {
  getBusinessProfile,
  saveBusinessProfile,
  uploadBusinessLogo,
} from '../services/documentService';

function maskUrl(url) {
  if (!url) return '';
  if (url.length <= 16) return url;
  return `••••••${url.slice(-10)}`;
}

function formatBsb(val) {
  const digits = (val || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
}

export default function Settings() {
  const { showToast, confirm } = useUI();
  const [settings, setSettings] = useState({
    businessName: '',
    legalBusinessName: '',
    abn: '',
    acn: '',
    businessEmail: '',
    businessPhone: '',
    businessWebsite: '',
    businessAddress: '',
    businessTagline: 'Business Process Automation Solutions',
    logoFileId: '',
    logoFileName: '',
    documentPrefix: 'SOW',
    currency: 'AUD',
    taxRate: 10,
    taxName: 'GST',
    defaultTnc: '',
    globalIntroEnabled: true,
    globalIntroHeading: '',
    globalIntroContent: '',
    globalFooterEnabled: true,
    globalFooterHeading: '',
    globalFooterContent: '',
    signatureEnabled: true,
    signaturePosition: 'beforeAppendix',
    signatureMarginTop: 30,
    signaturePaddingTop: 20,
    googleBackupUrl: '',
    googleBackupConnected: false,
    googleBackupLastSyncAt: '',
    googleAutoSyncEnabled: false,
    googleSyncInterval: 5,
  });

  const [bankingDetails, setBankingDetails] = useState({
    bankDetailsEnabled: false,
    bankName: '',
    bankAccountName: '',
    bankBsb: '',
    bankAccountNumber: '',
    bankPaymentReference: '',
  });

  const [showAccNum, setShowAccNum] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const data = await db.settings.get('global');
      if (data) {
        setSettings((prev) => ({ ...prev, ...data }));
        if (data.googleBackupConnected && data.googleBackupUrl) {
          try {
            const profile = await getBusinessProfile(data, true);
            if (profile) {
              setSettings((prev) => ({
                ...prev,
                businessName: profile.businessName || prev.businessName || '',
                legalBusinessName: profile.legalBusinessName || prev.legalBusinessName || '',
                abn: profile.abn || prev.abn || '',
                acn: profile.acn || prev.acn || '',
                businessEmail: profile.email || prev.businessEmail || '',
                businessPhone: profile.phone || prev.businessPhone || '',
                businessWebsite: profile.website || prev.businessWebsite || '',
                businessAddress: profile.address || prev.businessAddress || '',
                businessTagline: profile.tagline || prev.businessTagline || 'Business Process Automation Solutions',
                logoFileId: profile.logoFileId || prev.logoFileId || '',
                logoFileName: profile.logoFileName || prev.logoFileName || '',
              }));
              setBankingDetails({
                bankDetailsEnabled: profile.bankDetailsEnabled || false,
                bankName: profile.bankName || '',
                bankAccountName: profile.bankAccountName || '',
                bankBsb: profile.bankBsb || '',
                bankAccountNumber: profile.bankAccountNumber || '',
                bankPaymentReference: profile.bankPaymentReference || '',
              });
              if (profile.logoDataUrl) {
                setLogoPreviewUrl(profile.logoDataUrl);
              }
            }
          } catch (err) {
            console.warn('Could not load business profile from cloud:', err);
          }
        }
      }
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    await db.settings.put({ ...settings, id: 'global' });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
    showToast('Settings Saved Locally');
  };

  const handleSaveProfile = async () => {
    if (!settings.googleBackupConnected || !settings.googleBackupUrl) {
      showToast('Connect the Google Sheets workspace before saving business identity.', 'error');
      return;
    }
    if (bankingDetails.bankDetailsEnabled) {
      const bsbDigits = (bankingDetails.bankBsb || '').replace(/\D/g, '');
      const accDigits = (bankingDetails.bankAccountNumber || '').replace(/\D/g, '');
      if (!bankingDetails.bankAccountName || !bankingDetails.bankBsb || !bankingDetails.bankAccountNumber) {
        showToast('Account name, BSB, and Account number are required when banking details are enabled.', 'error');
        return;
      }
      if (bsbDigits.length !== 6) {
        showToast('BSB must contain exactly 6 digits.', 'error');
        return;
      }
      if (accDigits.length < 4 || accDigits.length > 12) {
        showToast('Account number must contain 4 to 12 digits.', 'error');
        return;
      }
    }
    setIsSavingProfile(true);
    try {
      const profilePayload = {
        businessName: settings.businessName,
        legalBusinessName: settings.legalBusinessName,
        abn: settings.abn,
        acn: settings.acn,
        email: settings.businessEmail,
        phone: settings.businessPhone,
        website: settings.businessWebsite,
        address: settings.businessAddress,
        tagline: settings.businessTagline,
        bankDetailsEnabled: bankingDetails.bankDetailsEnabled,
        bankName: bankingDetails.bankName,
        bankAccountName: bankingDetails.bankAccountName,
        bankBsb: bankingDetails.bankBsb,
        bankAccountNumber: bankingDetails.bankAccountNumber,
        bankPaymentReference: bankingDetails.bankPaymentReference,
      };
      const returnedProfile = await saveBusinessProfile(settings, profilePayload);
      const updatedSettings = {
        ...settings,
        businessName: returnedProfile.businessName || settings.businessName,
        legalBusinessName: returnedProfile.legalBusinessName || settings.legalBusinessName,
        abn: returnedProfile.abn || settings.abn,
        acn: returnedProfile.acn || settings.acn,
        businessEmail: returnedProfile.email || settings.businessEmail,
        businessPhone: returnedProfile.phone || settings.businessPhone,
        businessWebsite: returnedProfile.website || settings.businessWebsite,
        businessAddress: returnedProfile.address || settings.businessAddress,
        businessTagline: returnedProfile.tagline || settings.businessTagline,
        logoFileId: returnedProfile.logoFileId || settings.logoFileId,
        logoFileName: returnedProfile.logoFileName || settings.logoFileName,
      };
      await db.settings.put({ ...updatedSettings, id: 'global' });
      setSettings(updatedSettings);
      setBankingDetails({
        bankDetailsEnabled: returnedProfile.bankDetailsEnabled || false,
        bankName: returnedProfile.bankName || '',
        bankAccountName: returnedProfile.bankAccountName || '',
        bankBsb: returnedProfile.bankBsb || '',
        bankAccountNumber: returnedProfile.bankAccountNumber || '',
        bankPaymentReference: returnedProfile.bankPaymentReference || '',
      });
      showToast('Business Identity Saved to ScopeShield');
    } catch (err) {
      showToast(`Failed to save profile: ${err.message}`, 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 2 * 1024 * 1024) {
      showToast('Logo file size must be under 2 MB', 'error');
      return;
    }
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast('Only PNG, JPEG, and WebP images are allowed', 'error');
      return;
    }
    setIsUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target.result;
      try {
        if (!settings.googleBackupConnected || !settings.googleBackupUrl) {
          throw new Error('Cloud connection required for logo upload');
        }
        const result = await uploadBusinessLogo(settings, dataUrl, file.name);
        const updatedSettings = {
          ...settings,
          logoFileId: result.logoFileId,
          logoFileName: result.logoFileName,
        };
        await db.settings.put({ ...updatedSettings, id: 'global' });
        setSettings(updatedSettings);
        setLogoPreviewUrl(result.logoDataUrl || dataUrl);
        showToast('Logo Uploaded Successfully');
      } catch (err) {
        showToast(`Logo upload failed: ${err.message}`, 'error');
      } finally {
        setIsUploadingLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleConnect = async () => {
    try {
      const result = await testCloudConnection({ googleBackupUrl: settings.googleBackupUrl });
      if (result.connected) {
        await db.settings.update('global', {
          googleBackupUrl: settings.googleBackupUrl,
          googleBackupConnected: true,
        });
        setSettings((prev) => ({ ...prev, googleBackupConnected: true }));
        showToast('Cloud Endpoint Connected & Verified');
      }
    } catch (err) {
      await db.settings.update('global', { googleBackupConnected: false });
      setSettings((prev) => ({ ...prev, googleBackupConnected: false }));
      showToast(`Connection failed: ${err.message}`, 'error');
    }
  };

  const handleTestConnection = async () => {
    if (!settings.googleBackupUrl) return showToast('Please enter a Web App URL', 'error');
    try {
      const result = await testCloudConnection({ googleBackupUrl: settings.googleBackupUrl });
      if (result.connected) {
        showToast('Connection Successful!');
      }
    } catch (err) {
      showToast(`Connection failed: ${err.message}`, 'error');
    }
  };

  const handleSyncNow = async () => {
    if (!settings.googleBackupConnected) return showToast('Please connect first', 'error');
    setIsSyncing(true);
    try {
      const result = await pushWorkspaceBackup(settings);
      if (result.success) {
        const isoTimestamp = new Date().toISOString();
        await db.settings.update('global', { googleBackupLastSyncAt: isoTimestamp });
        setSettings((prev) => ({ ...prev, googleBackupLastSyncAt: isoTimestamp }));
        showToast('Backup Pushed to Cloud');
      }
    } catch (err) {
      showToast(`Cloud sync failed: ${err.message}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePullLatest = async () => {
    if (!settings.googleBackupConnected) return showToast('Please connect first', 'error');
    const isConfirmed = await confirm({
      title: 'Restore Cloud Backup?',
      message: 'This will OVERWRITE your current local data with the latest cloud version. Are you sure?',
      type: 'danger',
      confirmText: 'Yes, Restore Now',
      cancelText: 'Cancel',
    });
    if (!isConfirmed) return;
    setIsSyncing(true);
    try {
      const result = await restoreLatestBackup(settings);
      if (result.restored) {
        showToast(`Local Data Restored from Cloud (${result.proposalCount} proposals)`);
        setTimeout(() => window.location.reload(), 1000);
      } else if (result.reason === 'no_backup') {
        showToast('No backups found on cloud', 'error');
      }
    } catch (err) {
      showToast(`Restore failed: ${err.message}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleExport = async () => {
    const proposals = await db.proposals.toArray();
    const snippets = await db.snippets.toArray();
    const currentSettings = await db.settings.get('global');
    const { logoDataUrl, ...safeSettings } = currentSettings;
    const backupData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      proposals,
      snippets,
      settings: safeSettings,
    };
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scopeshield_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (!data.version) throw new Error('Invalid backup format');
        const merge = window.confirm('Do you want to MERGE with existing data? (Cancel to Overwrite Everything)');
        if (!merge) {
          await db.proposals.clear();
          await db.snippets.clear();
        }
        if (data.proposals) await db.proposals.bulkPut(data.proposals);
        if (data.snippets) await db.snippets.bulkPut(data.snippets);
        if (data.settings) await db.settings.put(data.settings);
        alert(`Import Successful!\nProposals: ${data.proposals?.length || 0}\nSnippets: ${data.snippets?.length || 0}`);
        window.location.reload();
      } catch (err) {
        alert('Failed to import. File might be corrupted.');
        console.error(err);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto w-full">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-500 mt-1 text-sm sm:text-base">Configure global defaults and manage data backups.</p>
      </div>
      <div className="space-y-8 pb-20">
        {/* Business Identity & Document Branding */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiAward} /> Business Identity & Document Branding
            </h2>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trading / Business Name</label>
                <input type="text" value={settings.businessName} onChange={(e) => setSettings({ ...settings, businessName: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Business name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Business Name</label>
                <input type="text" value={settings.legalBusinessName} onChange={(e) => setSettings({ ...settings, legalBusinessName: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Legal entity name" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ABN</label>
                <input type="text" value={settings.abn} onChange={(e) => setSettings({ ...settings, abn: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="ABN" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ACN</label>
                <input type="text" value={settings.acn} onChange={(e) => setSettings({ ...settings, acn: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="ACN" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Email</label>
                <input type="email" value={settings.businessEmail} onChange={(e) => setSettings({ ...settings, businessEmail: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Email address" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Phone</label>
                <input type="text" value={settings.businessPhone} onChange={(e) => setSettings({ ...settings, businessPhone: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Phone number" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Website</label>
              <input type="text" value={settings.businessWebsite} onChange={(e) => setSettings({ ...settings, businessWebsite: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="https://example.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
              <input type="text" value={settings.businessAddress} onChange={(e) => setSettings({ ...settings, businessAddress: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Address" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Tagline</label>
              <input type="text" value={settings.businessTagline} onChange={(e) => setSettings({ ...settings, businessTagline: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Business tagline" />
            </div>

            <div className="flex justify-end pt-4">
              <button onClick={handleSaveProfile} disabled={isSavingProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-semibold shadow-md transition-colors disabled:opacity-50">
                {isSavingProfile ? 'Saving...' : 'Save Business Identity'}
              </button>
            </div>
          </div>
        </div>

        {/* Global Terms & Conditions */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiLayout} /> Global Terms & Conditions
            </h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.globalFooterEnabled} onChange={(e) => setSettings({ ...settings, globalFooterEnabled: e.target.checked })} className="w-4 h-4 rounded text-indigo-600 focus:ring-0" />
              <span className="text-sm font-bold text-gray-600 uppercase tracking-tighter"> Enabled </span>
            </label>
          </div>
          <div className={`p-6 space-y-4 transition-opacity ${!settings.globalFooterEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <p className="text-sm text-gray-500">
              These terms are applied automatically to every Statement of Work. Use the proposal's Additional Terms section only for project-specific conditions.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"> Terms Section Heading </label>
              <input type="text" value={settings.globalFooterHeading} onChange={(e) => setSettings({ ...settings, globalFooterHeading: e.target.value })} className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="Terms & Conditions" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"> Global Terms Content </label>
              <RichTextEditor value={settings.globalFooterContent} onChange={(val) => setSettings({ ...settings, globalFooterContent: val })} enableLinks />
            </div>
          </div>
        </div>

        {/* General Settings */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiBriefcase} /> General Financial Settings
            </h2>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"> Currency </label>
                <select value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none">
                  <option value="AUD">AUD</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"> Tax Rate (%) </label>
                <input type="number" value={settings.taxRate} onChange={(e) => setSettings({ ...settings, taxRate: Number(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1"> Tax Name </label>
                <input type="text" value={settings.taxName} onChange={(e) => setSettings({ ...settings, taxName: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"> Default Terms & Conditions </label>
              <textarea value={settings.defaultTnc} onChange={(e) => setSettings({ ...settings, defaultTnc: e.target.value })} rows={4} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>

            <div className="flex justify-end pt-4">
              <button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg font-semibold shadow-md transition-colors">
                {isSaved ? 'Saved' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>

        {/* Data & Cloud */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiCloud} /> Data & Cloud Backup
            </h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"> Google Apps Script Web App URL </label>
              <div className="flex gap-2">
                <input type="text" value={settings.googleBackupUrl} onChange={(e) => setSettings({ ...settings, googleBackupUrl: e.target.value })} className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="https://script.google.com/macros/s/.../exec" />
                <button onClick={handleConnect} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700">Connect</button>
              </div>
              {settings.googleBackupConnected && <p className="text-xs text-green-600 mt-2 font-semibold">Connected: {maskUrl(settings.googleBackupUrl)}</p>}
            </div>

            <div className="flex flex-wrap gap-3">
              <button onClick={handleTestConnection} className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50">Test Connection</button>
              <button onClick={handleSyncNow} disabled={isSyncing} className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50">{isSyncing ? 'Syncing...' : 'Sync Now'}</button>
              <button onClick={handlePullLatest} disabled={isSyncing} className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50">Restore Latest</button>
            </div>

            <div className="border-t border-gray-200 pt-5">
              <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-sm font-semibold text-indigo-700 hover:text-indigo-900">
                {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
              </button>
              {showAdvanced && (
                <div className="mt-4 space-y-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={settings.googleAutoSyncEnabled} onChange={(e) => setSettings({ ...settings, googleAutoSyncEnabled: e.target.checked })} className="w-4 h-4 rounded text-indigo-600" />
                    <span className="text-sm text-gray-700">Automatic cloud sync</span>
                  </label>
                  <div className="max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sync Interval (minutes)</label>
                    <input type="number" min="1" value={settings.googleSyncInterval} onChange={(e) => setSettings({ ...settings, googleSyncInterval: Number(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-4 py-2" />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Local Backup</h3>
              <div className="flex gap-3">
                <button onClick={handleExport} className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50">Export Backup</button>
                <label className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 cursor-pointer">
                  Import Backup
                  <input type="file" accept="application/json" onChange={handleImport} className="hidden" />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Settings */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiEdit3} /> Signature Settings
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={settings.signatureEnabled} onChange={(e) => setSettings({ ...settings, signatureEnabled: e.target.checked })} className="w-4 h-4 rounded text-indigo-600" />
              <span className="text-sm text-gray-700">Include signature block in generated documents</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <select value={settings.signaturePosition} onChange={(e) => setSettings({ ...settings, signaturePosition: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2">
                  <option value="beforeAppendix">Before Appendix</option>
                  <option value="afterGlobalFooter">After Global Terms</option>
                  <option value="endOfDocument">End of Document</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Top Margin</label>
                <input type="number" value={settings.signatureMarginTop} onChange={(e) => setSettings({ ...settings, signatureMarginTop: Number(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-4 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Top Padding</label>
                <input type="number" value={settings.signaturePaddingTop} onChange={(e) => setSettings({ ...settings, signaturePaddingTop: Number(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-4 py-2" />
              </div>
            </div>
          </div>
        </div>

        {/* Banking Details */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiCreditCard} /> Banking Details
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={bankingDetails.bankDetailsEnabled} onChange={(e) => setBankingDetails({ ...bankingDetails, bankDetailsEnabled: e.target.checked })} className="w-4 h-4 rounded text-indigo-600" />
              <span className="text-sm font-semibold text-gray-700">Include banking details in SOW</span>
            </label>
            {bankingDetails.bankDetailsEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" value={bankingDetails.bankName} onChange={(e) => setBankingDetails({ ...bankingDetails, bankName: e.target.value })} placeholder="Bank name" className="border border-gray-300 rounded-lg px-4 py-2" />
                <input type="text" value={bankingDetails.bankAccountName} onChange={(e) => setBankingDetails({ ...bankingDetails, bankAccountName: e.target.value })} placeholder="Account name" className="border border-gray-300 rounded-lg px-4 py-2" />
                <input type="text" value={bankingDetails.bankBsb} onChange={(e) => setBankingDetails({ ...bankingDetails, bankBsb: formatBsb(e.target.value) })} placeholder="BSB" className="border border-gray-300 rounded-lg px-4 py-2" />
                <div className="relative">
                  <input type={showAccNum ? 'text' : 'password'} value={bankingDetails.bankAccountNumber} onChange={(e) => setBankingDetails({ ...bankingDetails, bankAccountNumber: e.target.value })} placeholder="Account number" className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-20" />
                  <button type="button" onClick={() => setShowAccNum(!showAccNum)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500">{showAccNum ? 'Hide' : 'Show'}</button>
                </div>
                <input type="text" value={bankingDetails.bankPaymentReference} onChange={(e) => setBankingDetails({ ...bankingDetails, bankPaymentReference: e.target.value })} placeholder="Payment reference" className="border border-gray-300 rounded-lg px-4 py-2 sm:col-span-2" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}